/**
 * GENERATED from source/variability.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const VARIABILITY = {
  "key": "types-of-variability",
  "title": "Types of Variability",
  "category": "Concepts",
  "platform": {
    "key": "types-of-variability",
    "duration": "8 min read",
    "chapter": {
      "id": "types-of-variability",
      "title": "Types of Variability"
    }
  },
  "cert": {
    "key": "types-of-variability-cert",
    "duration": "25 min"
  },
  "chapters": [
    {
      "id": "everywhere",
      "title": "Variability Is Everywhere",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Variability Is Everywhere"
        },
        {
          "kind": "text",
          "md": "Coaches hear the word **variability** constantly.\n\nAdd variability.\n\nMake practice more variable.\n\nLet athletes explore.\n\nAvoid repeating the exact same thing.\n\nBut variability can mean several different things.\n\nAt BAM, it helps to separate four ideas:\n\n1. **Movement variability** — the athlete’s movement changes.\n2. **Environmental variability** — the problem around the athlete changes.\n3. **Practice variability** — the coach intentionally changes practice conditions.\n4. **Functional movement variability** — the athlete changes movement in a useful way to continue solving the problem.\n\nThe goal is not to create as much variability as possible.\n\nThe goal is to understand:\n\n**What is changing?**\n\n**Why is it changing?**\n\n**Is that change helping the athlete?**"
        }
      ]
    },
    {
      "id": "movement",
      "title": "1. Movement Variability",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "1. Movement Variability"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Athlete Changes"
        },
        {
          "kind": "text",
          "md": "Even when an athlete tries to perform the exact same movement twice, the movement will never be perfectly identical.\n\nThere will be small differences in:\n\n- force production,\n- timing,\n- joint coordination,\n- balance,\n- and how the movement is organized.\n\nSome differences may be too small for a coach to see.\n\nThe athlete may not consciously notice them either.\n\nBut they exist.\n\nThink about shooting.\n\nA great shooter may make a long streak and then miss the next shot even though the setup looks almost identical.\n\nThat does not automatically mean there was a dramatic technical mistake.\n\nSometimes small differences in force, timing, coordination, attention, or other parts of the system are enough to change the outcome.\n\nThat is movement variability."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Movement Is Never Perfectly Repeatable",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Human movement contains variability even when the task appears identical.\n\nThe nervous system is not perfectly noiseless.\n\nForce production changes slightly.\n\nJoint timing changes.\n\nCoordination changes.\n\nBalance changes.\n\nAttention changes.\n\nThis is not automatically a failure of the human system.\n\nIt is part of being a human system.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Motor Noise Is Only Part of the Story",
          "blocks": [
            {
              "kind": "prose",
              "md": "It is tempting to describe all movement variability as **noise**.\n\nSome variability does arise from fluctuations in neural and muscular processes.\n\nBut modern motor-control research does not treat all variability as meaningless error.\n\nA system can allow some components of movement to vary while other components compensate, preserving the task outcome.\n\nSo the useful question is not simply:\n\n**How variable was the movement?**\n\nIt is:\n\n**Where did variability occur, and did it disrupt or support the task?**"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Bernstein and the Degrees-of-Freedom Problem",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This connects directly to Nikolai Bernstein.\n\nBernstein observed that humans possess many possible ways to organize movement.\n\nThe body contains huge numbers of degrees of freedom.\n\nDifferent joints, muscles, timings, and forces can combine in different ways to accomplish the same task.\n\nHis famous blacksmith observations showed that the exact joint movements varied across hammer strikes even when the endpoint of the hammer remained remarkably consistent.\n\nThat supports an important principle:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Skilled movement does not always require identical movement.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Sometimes skill means allowing variation in one part of the system so another part can remain stable.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Redundancy or Abundance?",
          "blocks": [
            {
              "kind": "prose",
              "md": "Historically, having many degrees of freedom was often described as a redundancy problem:\n\nHow does the nervous system control so many possible options?\n\nAnother perspective describes those options as **motor abundance**.\n\nThe extra degrees of freedom can provide flexibility.\n\nIf several movement solutions can accomplish the task, the athlete has options when the environment changes or one part of the system is disturbed.\n\nThis connects directly to functional movement variability."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Not Every Skill Needs the Same Amount of Stability"
        },
        {
          "kind": "text",
          "md": "The goal is not always to eliminate movement variability.\n\nDifferent skills tolerate different amounts and types of variability."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Shooting"
        },
        {
          "kind": "text",
          "md": "Shooting is highly precision-based.\n\nWe are trying to send a ball into a small target from more than 20 feet away, often using the entire body to create and transfer force.\n\nBecause of that, there can be value in stabilizing the base pattern and reducing unnecessary variability in the parts of the movement that matter most to the outcome.\n\nBut stability does not mean rigidity.\n\nA shooter may still need to organize around:\n\n- different staggered stances,\n- different momentum,\n- higher releases,\n- lower releases,\n- and different entries.\n\nWe can pursue both:\n\n**More stability in important components**\n\nand\n\n**More functional variability around them.**\n\nThose goals are not opposites."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Why Blocked Shooting Can Still Matter"
        },
        {
          "kind": "text",
          "md": "Sometimes we deliberately control the external environment.\n\nSame spot.\n\nSimilar setup.\n\nHigh repetition.\n\nA goal like:\n\n**Make 10 in a row.**\n\nOr:\n\n**Make 8 out of 10.**\n\nThe athlete does not need to consciously analyze every body part.\n\nTheir attention can remain externally focused on making the shot.\n\nBy controlling many outside variables, the athlete gets repeated exposure to a similar problem.\n\nAt BAM, one reason we use this is to create a more stable environment for calibration while the athlete continues to experience the small movement differences that naturally occur from rep to rep."
        },
        {
          "kind": "scienceNerd",
          "title": "Be Careful With the Mechanism",
          "blocks": [
            {
              "kind": "prose",
              "md": "We should avoid oversimplifying this as:\n\n**Blocked shooting teaches the cerebellum to remove motor noise.**\n\nThe cerebellum is important in many forms of sensorimotor adaptation, but basketball shooting is far more complex than one brain structure.\n\nThe useful coaching point is simpler:\n\n**Controlling external conditions changes the information available for learning and can make repeated action-outcome relationships easier to experience.**"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Finishing and Dribbling Can Use a Wider Solution Space"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Finishing"
        },
        {
          "kind": "text",
          "md": "The athlete may need different release angles:\n\n- wide,\n- narrow,\n- high,\n- low.\n\nThose differences are not automatically errors.\n\nThey may be exactly what allows the athlete to solve the environmental problem."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Dribbling"
        },
        {
          "kind": "text",
          "md": "A player may need:\n\n- a pocket dribble,\n- a wider dribble,\n- different dribble timing.\n\nAgain, variation is not automatically a technical flaw.\n\nIt may be functional.\n\nThis is why the optimal stability-variability balance is not the same for every skill."
        }
      ]
    },
    {
      "id": "environment",
      "title": "2. Environmental Variability",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "2. Environmental Variability"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Problem Changes"
        },
        {
          "kind": "text",
          "md": "Basketball itself is constantly changing.\n\nExamples include:\n\n- where the defense is,\n- where the pass comes from,\n- where the athlete’s attention goes,\n- starting position,\n- space,\n- timing,\n- time,\n- score,\n- and which decisions are available.\n\nThere are huge numbers of possible combinations.\n\nThis means athletes cannot rely on one exact movement solution working every time.\n\nThey need skills that function across changing conditions."
        },
        {
          "kind": "bigIdea",
          "md": "The environment changes, so the athlete has to be capable of changing with it."
        }
      ]
    },
    {
      "id": "practice",
      "title": "3. Practice Variability",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "3. Practice Variability"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Coach Changes the Problem"
        },
        {
          "kind": "text",
          "md": "Practice variability is what we intentionally manipulate.\n\nExamples BAM uses include changing:\n\n- the task,\n- the skill being performed,\n- the defense,\n- the number or type of decisions,\n- the pass,\n- time,\n- score,\n- starting position,\n- amount of space,\n- the start of a small-sided game,\n- or the order in which actions and concepts appear.\n\nPractice variability does not only belong in on-air drills.\n\nIt can happen inside:\n\n- small-sided games,\n- combo games,\n- team practice,\n- actions from different sides of the floor,\n- or sequences that interleave different concepts."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Practice Variability vs. Contextual Interference"
        },
        {
          "kind": "text",
          "md": "These ideas are related but not identical.\n\n**Practice variability** is the broad idea of changing practice conditions or problems.\n\n**Contextual interference** describes the interference created when different tasks or variations are practiced in an interleaved or changing order rather than repeated in one block.\n\nThat interference can make practice feel harder and look worse.\n\nBut harder-looking practice does not automatically mean worse learning."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Try This"
        },
        {
          "kind": "text",
          "md": "This is a demonstration, not a test.\n\nAnswer as quickly as possible."
        },
        {
          "kind": "timedRounds",
          "id": "multiply",
          "rounds": [
            {
              "label": "Round 1",
              "problems": [
                "8 × 7 = ?",
                "8 × 7 = ?",
                "8 × 7 = ?",
                "8 × 7 = ?",
                "8 × 7 = ?"
              ]
            },
            {
              "label": "Now Round 2:",
              "problems": [
                "8 × 7 = ?",
                "9 × 6 = ?",
                "4 × 9 = ?",
                "7 × 8 = ?",
                "6 × 7 = ?"
              ]
            }
          ]
        },
        {
          "kind": "text",
          "md": "The point is not whether you know multiplication.\n\nYou likely know every answer.\n\nThe difference is that Round 2 repeatedly forces you to retrieve and organize a new answer.\n\nRound 1 quickly becomes recitation.\n\nRound 2 keeps making you solve.\n\nThat is a simple analogy for why added interference can make performance slower in the moment."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Contextual Interference",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Low contextual interference typically groups similar repetitions together.\n\nHigher contextual interference mixes or interleaves tasks or variations.\n\nBAM examples include:\n\n- moving from a floater to a handle to a shot,\n- alternating different decisions,\n- moving between a 1v2 floater game and a 2v1 shooting game,\n- interleaving different actions in team practice,\n- changing sides of the floor,\n- or switching between concepts instead of repeating one for a long block.\n\nThis can make practice performance look worse.\n\nAnd that can be a feature rather than a bug.\n\nBut the science is more nuanced than:\n\n**Random practice is always better.**",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "What Does the Contextual Interference Research Say?",
          "blocks": [
            {
              "kind": "prose",
              "md": "A 2024 systematic review and meta-analysis examined delayed retention across 54 studies.\n\nAcross the overall evidence base, higher contextual interference produced a moderate benefit for retention.\n\nBut the result changed substantially depending on context.\n\nThe benefit was stronger in laboratory studies.\n\nIn applied field studies involving sport skills, the benefit was much smaller.\n\nThe pooled effect in younger participants was also far less convincing.\n\nSo the useful conclusion is:\n\n> **Contextual interference can help learning, but the evidence does not justify randomizing everything in sport practice.**"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Performance Is Not Learning",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "One reason contextual interference matters is that it exposes a common coaching mistake.\n\nWe see an athlete struggling and think:\n\n**This practice is not working.**\n\nOr we see them succeeding repeatedly and think:\n\n**They learned it.**\n\nNeither conclusion is guaranteed.\n\nImmediate performance and longer-term learning are related, but they are not identical.\n\nMore predictable practice can create rapid success.\n\nMore variable practice can temporarily create slower, uglier performance.\n\nThat is why we need to look beyond today’s success percentage.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Less Variability Is Not Automatically Worse"
        },
        {
          "kind": "text",
          "md": "There are times when lower variability is useful.\n\nWe may reduce variability when:\n\n- an athlete is early in learning something,\n- we want to build confidence,\n- we need a high volume of repetitions,\n- we want to isolate a limiting component,\n- the athlete needs more success and buy-in,\n- or the skill already contains enough movement variability.\n\nMore variable practice can create richer problems.\n\nBut extra variability can also become unnecessary noise.\n\nThe question remains:"
        },
        {
          "kind": "bigIdea",
          "md": "What does this athlete need from this practice right now?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Environmental Variability Helps Us Choose Practice Variability"
        },
        {
          "kind": "text",
          "md": "One of the best ways to decide what to vary is to look at what varies in the game.\n\nImagine designing a finishing small-sided game.\n\nAsk:\n\n**What actually changes when this problem occurs in basketball?**\n\nBAM variables can include:\n\n- where the defense comes from,\n- where the pass comes from,\n- starting position,\n- space,\n- time,\n- score,\n- or what decision becomes available.\n\nNow the practice variability has a reason.\n\nWe do not need to vary all of them at once."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Variability Inside Small-Sided Games"
        },
        {
          "kind": "text",
          "md": "Variability is not limited to on-air drills.\n\nSmall-sided games can be manipulated through:\n\n- the start,\n- space,\n- defender positioning or timing,\n- the decisions available,\n- or the task that comes before or after the game.\n\nBAM can also use **combo games**.\n\nFor example:\n\nMove from a **1v2 floater game** into a **2v1 shooting game**, then back again.\n\nNow the athlete is repeatedly changing the problem they need to solve.\n\nThe same principle can apply in team practice by interleaving:\n\n- different actions,\n- different sides of the floor,\n- and different concepts."
        }
      ]
    },
    {
      "id": "functional",
      "title": "4. Functional Movement Variability",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "4. Functional Movement Variability"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Athlete Changes in a Useful Way"
        },
        {
          "kind": "text",
          "md": "Now everything connects.\n\nMovement will always contain variability.\n\nThe environment will always contain variability.\n\nSo the goal cannot simply be:\n\n**Move exactly the same way every time.**\n\nThe athlete needs the ability to reorganize movement while still achieving the goal.\n\nThat is functional movement variability."
        },
        {
          "kind": "bigIdea",
          "md": "Change what needs to change while preserving what needs to stay stable."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Functional vs. Dysfunctional Variability",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Dysfunctional variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Movement changes in ways that reduce the athlete’s ability to achieve the goal.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Functional variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Movement changes in ways that allow the athlete to preserve or improve the goal as conditions change.\n\nThis means variability itself tells us very little.\n\nWe need to know:\n\n**What changed?**\n\n**Why did it change?**\n\n**What happened to the outcome?**",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Shooting"
        },
        {
          "kind": "text",
          "md": "In shooting, we may want a relatively stable base pattern.\n\nBut game conditions still require adjustment.\n\nA player may shoot from different staggered stances.\n\nMomentum may change.\n\nThe release may need to become higher or lower.\n\nImportant pieces may stay relatively stable while other parts of the movement reorganize around them.\n\nThe goal is not an identical movement.\n\nThe goal is a movement stable enough to maintain precision while adaptable enough to deal with the situation."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Finishing"
        },
        {
          "kind": "text",
          "md": "Finishing allows a wider movement solution space.\n\nThe athlete may need:\n\n- wide release angles,\n- narrow release angles,\n- high releases,\n- low releases.\n\nThose differences may be exactly what allows the athlete to solve the problem."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Dribbling"
        },
        {
          "kind": "text",
          "md": "A player may need:\n\n- a pocket dribble,\n- a wider dribble,\n- different dribble timing.\n\nAgain, the larger goal remains functional.\n\nThe movement changes to make the goal possible."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Stability and Adaptability"
        },
        {
          "kind": "text",
          "md": "This is the central idea.\n\nGreat skill is not necessarily the ability to reproduce one movement perfectly.\n\nAnd great skill is not the ability to move differently every time.\n\nIt is the ability to be:"
        },
        {
          "kind": "bigIdea",
          "md": "Stable where stability matters."
        },
        {
          "kind": "text",
          "md": "and"
        },
        {
          "kind": "bigIdea",
          "md": "Variable where adaptability matters."
        },
        {
          "kind": "text",
          "md": "Different skills require different balances.\n\nDifferent athletes require different balances.\n\nDifferent stages of learning require different balances.\n\nThat is the coaching problem."
        }
      ]
    },
    {
      "id": "identify",
      "title": "ACTIVITY 1: Identify the Type of Variability",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 1: Identify the Type of Variability",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For each example, identify the primary form of variability being emphasized.",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "type-a",
          "prompt": "**A**\n\nA shooter performs repeated shots from the same spot, but exact force and timing differ slightly on every repetition.",
          "options": [
            {
              "id": "movement",
              "label": "Movement variability"
            },
            {
              "id": "environmental",
              "label": "Environmental variability"
            },
            {
              "id": "practice",
              "label": "Practice variability"
            },
            {
              "id": "functional",
              "label": "Functional movement variability"
            }
          ],
          "discussion": "**Movement variability**",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "type-b",
          "prompt": "**B**\n\nA finishing task changes where the defense comes from and where the pass arrives from.",
          "options": [
            {
              "id": "movement",
              "label": "Movement variability"
            },
            {
              "id": "environmental",
              "label": "Environmental variability"
            },
            {
              "id": "practice",
              "label": "Practice variability"
            },
            {
              "id": "functional",
              "label": "Functional movement variability"
            }
          ],
          "discussion": "**Environmental variability intentionally incorporated as practice variability**",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "type-c",
          "prompt": "**C**\n\nA player alternates between a floater, handle, and shot instead of completing all repetitions of one skill first.",
          "options": [
            {
              "id": "movement",
              "label": "Movement variability"
            },
            {
              "id": "environmental",
              "label": "Environmental variability"
            },
            {
              "id": "practice",
              "label": "Practice variability"
            },
            {
              "id": "functional",
              "label": "Functional movement variability"
            }
          ],
          "discussion": "**Practice variability / contextual interference**",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "type-d",
          "prompt": "**D**\n\nA player shoots from different staggered stances and adjusts the rest of the movement to maintain the shot.",
          "options": [
            {
              "id": "movement",
              "label": "Movement variability"
            },
            {
              "id": "environmental",
              "label": "Environmental variability"
            },
            {
              "id": "practice",
              "label": "Practice variability"
            },
            {
              "id": "functional",
              "label": "Functional movement variability"
            }
          ],
          "discussion": "**Functional movement variability**",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "type-e",
          "prompt": "**E**\n\nA player uses a pocket dribble on one repetition and a wider dribble with different timing on another because the situation requires it.",
          "options": [
            {
              "id": "movement",
              "label": "Movement variability"
            },
            {
              "id": "environmental",
              "label": "Environmental variability"
            },
            {
              "id": "practice",
              "label": "Practice variability"
            },
            {
              "id": "functional",
              "label": "Functional movement variability"
            }
          ],
          "discussion": "**Functional movement variability**",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The categories can overlap.\n\nThese are lenses, not perfect boxes.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 2: Stabilize or Expand?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "stabilize",
          "title": false,
          "numbered": false,
          "intro": "For each skill, decide what you would currently prioritize:\n\n**More stability**\n\n**More functional variability**\n\n**Both**\n\nThen explain why.",
          "steps": [
            {
              "id": "shooting",
              "q": "Shooting",
              "hint": "Consider:\n\n- stable base pattern,\n- staggered stances,\n- high and low releases,\n- changing momentum.\n\nWhat should stay relatively stable?\n\nWhat should be allowed to adapt?",
              "rows": 3
            },
            {
              "id": "finishing",
              "q": "Finishing",
              "hint": "Consider:\n\n- wide release,\n- narrow release,\n- high release,\n- low release.\n\nHow much solution space should the athlete have?\n\nWhat makes variability functional rather than random?",
              "rows": 3
            },
            {
              "id": "dribbling",
              "q": "Dribbling",
              "hint": "Consider:\n\n- pocket dribble,\n- wide dribble,\n- different dribble timing.\n\nWhat does the athlete need to control?\n\nWhat should remain adaptable?",
              "rows": 3
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "design",
      "title": "ACTIVITY 3: Build Variability From the Game Backward",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 3: Build Variability From the Game Backward",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "backward",
          "title": false,
          "numbered": false,
          "intro": "Choose one activity you already coach.\n\nDo not create a new basketball scenario.",
          "steps": [
            {
              "id": "goal",
              "q": "Step 1: Define the goal",
              "hint": "What is the athlete trying to become better at?",
              "rows": 3
            },
            {
              "id": "environment",
              "q": "Step 2: Identify environmental variability",
              "hint": "What actually changes when this problem occurs in competition?\n\nExamples BAM considers include:\n\n- defense,\n- pass,\n- starting position,\n- space,\n- time,\n- score,\n- decisions,\n- actions,\n- and side of the floor.\n\nOnly choose variables that actually matter to your activity.",
              "rows": 3
            },
            {
              "id": "stability",
              "q": "Step 3: Identify movement stability",
              "hint": "Which parts of the athlete’s solution would you like to become more stable?",
              "rows": 3
            },
            {
              "id": "movement",
              "q": "Step 4: Identify useful movement variability",
              "hint": "Where do you want the athlete to have more movement options?",
              "rows": 3
            },
            {
              "id": "practice",
              "q": "Step 5: Choose practice variability",
              "hint": "Which environmental variable should you manipulate in practice?\n\nDo not choose five.\n\nChoose the smallest change that solves the problem.",
              "rows": 3
            },
            {
              "id": "interference",
              "q": "Step 6: Decide how much interference to create",
              "hint": "Would you:\n\n- block repetitions,\n- alternate between two variations,\n- interleave several skills,\n- use a combo game,\n- manipulate the defense,\n- or let the small-sided game generate variability naturally?\n\nExplain why.",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 4: When Does Variability Become Noise?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Think about the activity you just designed.\n\nNow imagine adding:\n\n- a different pass,\n- a different defender,\n- a different start,\n- less space,\n- a new decision,\n- a new score constraint,\n- and another skill between each repetition.\n\nWould that automatically improve learning?\n\nNo.\n\nRemove variables until you reach the smallest amount of variability that still creates the adaptation you want.",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "noise",
          "prompt": "Explain why you kept each one.",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Add enough variability to create adaptation without adding so much that the actual learning target disappears.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "map",
      "title": "A Simple Map",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Simple Map"
        },
        {
          "kind": "framework",
          "steps": [
            {
              "q": "MOVEMENT VARIABILITY",
              "md": "The athlete changes."
            },
            {
              "q": "ENVIRONMENTAL VARIABILITY",
              "md": "The problem changes."
            },
            {
              "q": "PRACTICE VARIABILITY",
              "md": "We manipulate practice to prepare for both."
            },
            {
              "q": "FUNCTIONAL MOVEMENT VARIABILITY",
              "md": "The athlete becomes stable enough to perform and adaptable enough to adjust."
            }
          ]
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Pick one skill you coach often.\n\nAnswer four questions:",
          "steps": [
            {
              "id": "stable",
              "q": "1. What needs to become more stable?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "adaptable",
              "q": "2. What needs to become more adaptable?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "environment",
              "q": "3. What environmental changes does the athlete need to handle?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "manipulate",
              "q": "4. What should I manipulate in practice to prepare them for those changes?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "If you can answer those four questions, “add variability” becomes much more specific.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "Human movement varies.\n\nBasketball varies.\n\nPractice can vary.\n\nBut variability is not automatically learning.\n\nThe coach’s job is to distinguish between:\n\n**noise**\n\n**useful exploration**\n\n**necessary stability**\n\nand\n\n**functional adaptation.**\n\nFor a precision-based skill like shooting, we may deliberately stabilize important components while developing adaptability around them.\n\nFor skills like finishing or dribbling, we may encourage a broader movement solution space.\n\nPractice variability should expose athletes to meaningful changes, not arbitrary randomness.\n\nAnd contextual interference can be useful without becoming a religion."
        },
        {
          "kind": "bigIdea",
          "md": "The goal is not more variability."
        },
        {
          "kind": "bigIdea",
          "md": "The goal is more functional variability."
        },
        {
          "kind": "text",
          "md": "Stable enough to perform.\n\nAdaptable enough to solve."
        },
        {
          "kind": "reflection",
          "md": "Pick one skill you train often. What do you want to become more stable, and where do you actually want the athlete to have more ways to solve the problem?",
          "depth": "platform"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            },
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            }
          ]
        }
      ]
    }
  ]
};
